import {Chart, ChartConfiguration} from 'chart.js';
import {Context} from 'chartjs-plugin-datalabels';
import dayjs from 'dayjs';
import {useCallback, useEffect, useMemo, useRef} from 'react';

import {ChartTooltip} from '../../../../component/chart-tooltip/chart-tooltip';
import {ChartTooltipRow} from '../../../../component/chart-tooltip/chart-tooltip-row';
import {ChartTooltipTitle} from '../../../../component/chart-tooltip/chart-tooltip-title';
import {Text} from '../../../../component/text/text';
import {
    lps_semi_circle_progress_high_rating_color,
    lps_semi_circle_progress_low_rating_color,
    lps_semi_circle_progress_medium_rating_color,
} from '../../../../css/var-export.scss';
import {customReactChartTooltip} from '../../../../layout/chart/utils/chart-helper';
import {useLpsAverageRating} from '../../../../service/dashboard/dashboard-lps/dashboard-lps-hook';
import {LpsStatus} from '../lps-rating-status/lps-status/lps-status';

export function LpsChart(): JSX.Element {
    const {startDate, endDate} = useMemo(
        () => ({
            startDate: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
            endDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
        }),
        []
    );

    const {data: apiData} = useLpsAverageRating({startDate, endDate});

    const data = useMemo(
        () =>
            apiData?.map((item) => ({
                date: item.date,
                rating: item.avgLps || 0,
            })) || [],
        [apiData]
    );

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const chartRef = useRef<Chart | null>(null);

    const getRatingColor = useCallback((rating: number): string => {
        if (rating > 80) {
            return lps_semi_circle_progress_high_rating_color;
        }

        if (rating >= 50) {
            return lps_semi_circle_progress_medium_rating_color;
        }

        return lps_semi_circle_progress_low_rating_color;
    }, []);

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const chartConfig: ChartConfiguration<'line'> = useMemo(() => {
        const labels = data.map((point) => {
            const date = new Date(point.date);

            return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}`;
        });

        const interpolatedData: Array<number> = [];
        const interpolatedLabels: Array<string> = [];
        const originalIndices: Array<number> = [];

        data.forEach((current, index) => {
            interpolatedData.push(current.rating);
            interpolatedLabels.push(labels[index] || '');
            originalIndices.push(index);

            const next = data[index + 1];

            if (next) {
                const thresholds = [50, 80];
                const min = Math.min(current.rating, next.rating);
                const max = Math.max(current.rating, next.rating);

                thresholds.forEach((threshold) => {
                    if (min < threshold && max > threshold) {
                        const ratio = (threshold - current.rating) / (next.rating - current.rating);

                        if (ratio > 0 && ratio < 1) {
                            interpolatedData.push(threshold);
                            interpolatedLabels.push('');
                            originalIndices.push(-1);
                        }
                    }
                });
            }
        });

        return {
            type: 'line',
            data: {
                labels: interpolatedLabels,
                datasets: [
                    {
                        label: 'Rating',
                        data: interpolatedData,
                        borderWidth: 4,
                        pointRadius: 0,
                        segment: {
                            borderColor: (context) => {
                                const rating = context.p1.parsed.y;

                                return getRatingColor(rating);
                            },
                        },
                    },
                ],
            },
            options: {
                maintainAspectRatio: false,
                interaction: {
                    intersect: false,
                    mode: 'index',
                },
                hover: {
                    intersect: false,
                    mode: 'index',
                },

                elements: {
                    line: {
                        tension: 0.4,
                    },
                },

                scales: {
                    x: {
                        border: {
                            display: false,
                        },
                        type: 'category',
                        grid: {
                            color: '#E5E7EB',
                            drawOnChartArea: false,
                            drawTicks: false,
                        },
                        ticks: {
                            color: '#6B7280',
                            maxRotation: 45,
                            minRotation: 0,
                            callback: (_value, index) => {
                                const label = interpolatedLabels[index];

                                if (!label) {
                                    return '';
                                }

                                const originalIndex = originalIndices[index];

                                // eslint-disable-next-line no-undefined
                                if (originalIndex === -1 || originalIndex === undefined) {
                                    return '';
                                }

                                const isWeekly = originalIndex % 7 === 0;
                                const isLastOriginalDate = originalIndex === data.length - 1;

                                return isWeekly || isLastOriginalDate ? label : '';
                            },
                        },
                    },
                    y: {
                        min: 0,
                        max: 100,
                        grid: {
                            color: '#E5E7EB',
                            drawBorder: false,
                            drawTicks: false,
                        },
                        ticks: {
                            color: '#6B7280',
                            stepSize: 20,
                        },
                    },
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        enabled: false,
                        external: (context: Context) =>
                            customReactChartTooltip({
                                context,
                                // eslint-disable-next-line react/no-unstable-nested-components
                                componentRenderer: (index, tooltipRendererOptions) => {
                                    const originalIndex = originalIndices[index];

                                    // eslint-disable-next-line no-undefined
                                    if (originalIndex === -1 || originalIndex === undefined) {
                                        return <div />;
                                    }

                                    const rating = Math.round(interpolatedData[index] || 0);
                                    const dateString = dayjs(data[originalIndex]?.date).format('DD.MM.YYYY');

                                    return (
                                        <ChartTooltip position={tooltipRendererOptions?.position}>
                                            <ChartTooltipTitle title={dateString} />
                                            <ChartTooltipRow
                                                label="LPS"
                                                value={
                                                    <div>
                                                        <Text>{rating}</Text> <LpsStatus value={rating} />
                                                    </div>
                                                }
                                            />
                                        </ChartTooltip>
                                    );
                                },
                            }),
                    },
                    datalabels: {
                        display: false,
                    },
                },
            },
        };
    }, [data, getRatingColor]);

    useEffect(() => {
        if (canvasRef.current) {
            if (chartRef.current) {
                chartRef.current.destroy();
            }

            chartRef.current = new Chart(canvasRef.current, chartConfig);
        }

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
            }
        };
    }, [chartConfig]);

    return (
        <div style={{width: '100%', height: '400px'}}>
            <canvas ref={canvasRef} />
        </div>
    );
}
